/*
 * From https://www.redblobgames.com/grids/voronoi/
 * Copyright 2018 Red Blob Games <redblobgames@gmail.com>
 * License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 *
 * Render triangle and region views in webgl
 */
'use strict';

/* global createREGL */

const {mat4} = require('gl-matrix');
const makeRegl = createREGL;

const util            = require('./util');
const MeshBuilder     = require('@redblobgames/dual-mesh/create');
const {makeRandFloat} = require('@redblobgames/prng');
const Poisson         = require('poisson-disk-sampling');


const centerFn = util.circumcenter; // or incenter

const mesh_spacing = 30;

class Float32ArrayBuilder {
    constructor (N) {
        this.array = new Float32Array(N);
        this.p = 0;
    }

    push(...array) {
        for (let i = 0; i < array.length; i++) {
            this.array[this.p++] = array[i];
        }
    }
}


function drawTriangles(canvas, mesh) {
    console.time('build');
    let a_position = new Float32ArrayBuilder(2 * 3 * mesh.numSolidTriangles);
    let a_barycentric = new Float32ArrayBuilder(3 * 3 * mesh.numSolidTriangles);
    let a_color = new Float32ArrayBuilder(4 * 3 * mesh.numSolidTriangles);
    for (let t = 0; t < mesh.numSolidTriangles; t++) {
        for (let i = 0; i < 3; i++) {
            let s = t*3 + i;
            let r1 = mesh.s_begin_r(s);
            a_position.push(mesh.r_x(r1), mesh.r_y(r1));
            a_barycentric.push(i===0? 1 : 0, i===1?  1 : 0, i===2? 1 : 0);
            a_color.push(1.0-t/mesh.numTriangles, 0.5-Math.sqrt(t/mesh.numTriangles), t/mesh.numTriangles, 1.0);
        }
    }
    console.timeEnd('build');

    let regl = makeRegl({canvas: canvas, extensions: ['OES_standard_derivatives']});

    console.time('setup');
    let setup = regl({
        vert:   `
  precision highp float;
  uniform mat4 u_view;

  attribute vec2 a_position;
  attribute vec3 a_barycentric;
  attribute vec4 a_color;

  varying vec3 v_barycentric;
  varying vec4 v_color;

  void main() {
    v_color = a_color;
    v_barycentric = a_barycentric;
    gl_Position = u_view * vec4(a_position, 0, 1);
  }`,

        // see http://codeflow.org/entries/2012/aug/02/easy-wireframe-display-with-barycentric-coordinates/
        frag: `
  #extension GL_OES_standard_derivatives : enable
  precision highp float;

  uniform vec2 u_linewidth;

  varying vec3 v_barycentric;
  varying vec4 v_color;

  float shade(float start, float end, vec3 barycentric) {
    vec3 slope = fwidth(barycentric);
    vec3 from_axis = smoothstep(slope * start, slope * end, barycentric);
    return min(from_axis.z, min(from_axis.x, from_axis.y));
  }

  void main() {
    float t = shade(u_linewidth.x, u_linewidth.y, v_barycentric)
            * (1.0 - shade(0.0, 50.0, v_barycentric));
    gl_FragColor = vec4(v_color.rgb * t, 1);
  }`,

        // why does regl default to one-one?
        blend: {
            enable: true,
            func: {
                src: 'src alpha',
                dst: 'one minus src alpha'
            }
        },
        
        attributes: {
            a_position: a_position.array,
            a_barycentric: a_barycentric.array,
            a_color: a_color.array,
        },

        uniforms: {
            u_linewidth: [
                20 / Math.sqrt(a_position.array.length),
                100 / Math.sqrt(a_position.array.length),
            ],
            u_view: regl.prop('u_view'),
        },

        count: a_position.p / 2,
    });
    console.timeEnd('setup');
    
    let props = {u_view:
                 mat4.translate([],
                                mat4.scale([],
                                           mat4.create(),
                                           [1.9/1000, 1.9/1000, 1]),
                                [-500, -500, 0]),
                };
    
    console.time('draw');
    regl.clear({
        color: [0, 0, 0, 1],
        depth: 1,
    });
    setup(props);
    console.timeEnd('draw');
    console.log('triangle rendering with', a_position.p/6, 'triangles');
}



function drawRegions(canvas, mesh) {
    let a_position = new Float32ArrayBuilder(2 * 3 * mesh.numSides);
    let a_barycentric_edge = new Float32ArrayBuilder(1 * 3 * mesh.numSides);
    let a_color = new Float32ArrayBuilder(4 * 3 * mesh.numSides);
    let array_s = [];
    let t_vertex = util.calculateAlternateCenterPositions(mesh, centerFn);
    console.time('build');
    for (let r = 0; r < mesh.numRegions; r++) {
        if (!mesh.r_ghost(r)) {
            // Produce a GL triangle for each of the regions edges, formed
            // from points 'r', 't1', 't2'
            mesh.r_circulate_s(array_s, r);
            for (let s of array_s) {
                let t1 = mesh.s_inner_t(s),
                    t2 = mesh.s_outer_t(s);
                a_position.push(mesh.r_x(r), mesh.r_y(r),
                                t_vertex[t1][0], t_vertex[t1][1],
                                t_vertex[t2][0], t_vertex[t2][1]);
                a_barycentric_edge.push(1, 0, 0);
                a_color.push(
                    1.0-r/mesh.numRegions, 0.5-Math.sqrt(r/mesh.numRegions), r/mesh.numRegions, 1.0,
                    1.0-t1/mesh.numTriangles, 0.5-Math.sqrt(t1/mesh.numTriangles), t1/mesh.numTriangles, 1.0,
                    1.0-t2/mesh.numTriangles, 0.5-Math.sqrt(t2/mesh.numTriangles), t2/mesh.numTriangles, 1.0
                );
            }
        }
    }
    console.timeEnd('build');
    
    let regl = makeRegl({canvas: canvas, extensions: ['OES_standard_derivatives']});

    console.time('setup');
    let setup = regl({
        vert:   `
  precision highp float;
  uniform mat4 u_view;

  attribute vec2 a_position;
  attribute float a_barycentric_edge;
  attribute vec4 a_color;

  varying float v_barycentric_edge;
  varying vec4 v_color;

  void main() {
    v_color = a_color;
    v_barycentric_edge = a_barycentric_edge;
    gl_Position = u_view * vec4(a_position, 0, 1);
  }`,

        frag: `
  #extension GL_OES_standard_derivatives : enable
  precision highp float;

  uniform vec2 u_linewidth;

  varying float v_barycentric_edge;
  varying vec4 v_color;

  void main() {
    float d = fwidth(v_barycentric_edge);
    float t = smoothstep(d * u_linewidth.x, d * u_linewidth.y, v_barycentric_edge);
    gl_FragColor = vec4(v_color.rgb * t, 1);
  }`,

        // why does regl default to one-one?
        blend: {
            enable: true,
            func: {
                src: 'src alpha',
                dst: 'one minus src alpha'
            }
        },
        
        attributes: {
            a_position: a_position.array,
            a_barycentric_edge: a_barycentric_edge.array,
            a_color: a_color.array,
        },

        uniforms: {
            u_linewidth: [
                40 / Math.sqrt(a_position.array.length),
                200 / Math.sqrt(a_position.array.length),
            ],
            u_view: regl.prop('u_view'),
        },

        count: a_position.p / 2,
    });
    console.timeEnd('setup');

    let props = {u_view:
                 mat4.translate([],
                                mat4.scale([],
                                           mat4.create(),
                                           [1.75/1000, 1.75/1000, 1]),
                                [-500, -500, 0]),
                };
    console.time('draw');
    regl.clear({
        color: [0, 0, 0, 1],
        depth: 1,
    });
    setup(props);
    console.timeEnd('draw');

    console.log('polygon rendering with', a_position.p/6, 'triangles');
    return {regl, setup};
}



function blended() {
    let mesh = new MeshBuilder({boundarySpacing: mesh_spacing * Math.sqrt(2) })
        .addPoints(util.makeSquareGrid(mesh_spacing).filter(p => (p[1]/100 | 0) % 3 === 0))
        .addPoints(util.makeHexGrid(mesh_spacing).filter(p => (p[1]/100 | 0) % 3 !== 0))
    // .addPoints(util.makeRadialGrid(50))
    // .addPoisson(Poisson, mesh_spacing, makeRandFloat(12345))
    //    .addPoints(util.makeSquareGrid(200))
        .create(true);

    console.log(`count: ${mesh.numTriangles} triangles, ${mesh.numRegions} regions`);
    drawTriangles(document.getElementById("render-triangles"), mesh);
    drawRegions(document.getElementById("render-regions"), mesh);
}

function animated() {
    console.time('construct mesh');
    let mesh = new MeshBuilder({boundarySpacing: 10})
    //    .addPoints(util.makeSquareGrid(2).filter(p => (p[1]/100 | 0) % 3 === 0))
    //    .addPoints(util.makeHexGrid(2).filter(p => (p[1]/100 | 0) % 3 !== 0))
        .addPoints(util.makeRadialGrid(10).filter(p => p[0] < 300))
        .addPoisson(Poisson, 15, makeRandFloat(12345))
    //    .addPoints(util.makeSquareGrid(200))
        .create(true);
    console.timeEnd('construct mesh');

    console.log(`count: ${mesh.numTriangles} triangles, ${mesh.numRegions} regions`);
    drawTriangles(document.getElementById("render-triangles"), mesh);
    let regions = drawRegions(document.getElementById("render-regions"), mesh);
    regions.regl.frame(context => {
        let mat = mat4.create();
        let scale = 9 + Math.sin(context.time*0.1*(1+0.2*Math.cos(context.time*0.05)) + 5);
        mat4.scale(mat, mat, [scale/1000, scale/1000, 1]);
        mat4.rotateZ(mat, mat, Math.cos(0.2*context.time*(0.1 + 0.02 * Math.sin(context.time*0.03))) * Math.PI);
        mat4.translate(mat, mat, [-500 + 300*Math.cos(context.time*0.33), -500 + 300*Math.sin(context.time*0.54), 0]);
        regions.regl.clear({
            color: [0, 0, 0, 1],
            depth: 1,
        });
        regions.setup({
            u_view: mat
        });
    });
}

animated();

// TODO: for matrix aspect ratio be sure to see https://webglfundamentals.org/webgl/lessons/webgl-anti-patterns.html

